import { IQuery, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import { ORM } from 'utility-ts-orm';
import { IAPCoursesDatasource } from '../../../../global/datasources/ap_courses_datasource';
import { APDegreeMapQueryHelper } from '../../../../plan/queries/ap_degree_map_query_helper';
import { APCourse } from '../../../builders/ap_course_builder';
import { APGenericSlotBuilder, CourseSlot } from '../../../builders/ap_generic_slot_builder';
import { APPlannedCourse, IAPPlannedCourseMeta } from '../../../builders/ap_planned_course_builder';
import { IAPProgramSlotMeta, SingleCourseProgramSlot } from '../../../builders/ap_program_slot_builder';
import { INVALID_ID } from '../../../constants/ap_id_constants';
import { IAPPlannedCourseDataSource } from '../../../datasources/ap_planned_course_datasource';
import { IAPProgramSlotDatasource } from '../../../datasources/ap_program_slot_datasource';
import { APCourseServant } from '../../../servants/ap_course_servant';
import { APCourseInfoModel, AssociatedCourse, IAPCourseRequisite } from '../models/course_info_model';
import { ReqServant } from '../servants/course_req_servant';
import { ReqNode } from '../servants/course_req_tree';
import { APMinGradeServant } from './../../../servants/ap_min_grade_servant';
import { APProgramSlotServant } from './../../../servants/ap_program_slot_servant';
import { APCourseDetailCourse, IAPCourseInfoModel } from './../models/course_info_model';

export class APCourseInfoQuery implements IQuery {
  private isFromSimpleSmartCatSearch = false;
  private _result: {
    course: APCourseDetailCourse;
    preReqHtml?: string;
    coReqHtml?: string;
    hasCoReqs: boolean;
    hasPreReqs: boolean;
    hasPreReqIssues: boolean;
    hasTests: boolean;
    hasMultiSections: boolean;
    multiSectionLabel: string;
    genericSlot: CourseSlot;
    showSmartCatAddToTermOption: boolean;
    coursePlanningSource: string;
    showSmartCatRemoveFromTermOption: boolean;
  };
  constructor(
    private ngAPCourseInfoModel: APCourseInfoModel,
    private $state: ng.ui.IStateService,
    private ngAPPlannedCoursesDatasource: IAPPlannedCourseDataSource,
    private ngAPDegreeMapQueryHelper: APDegreeMapQueryHelper,
    private ngAPProgramSlotDatasource: IAPProgramSlotDatasource,
    private appConfigModel: any,
    private ngAPCoursesDatasource: IAPCoursesDatasource
  ) {
    ORM.registerQuery(this, ngAPCourseInfoModel, ngAPProgramSlotDatasource, ngAPPlannedCoursesDatasource);
  }

  isLockedByStatus(
    plannedCourse: APPlannedCourse & IAPPlannedCourseMeta,
    programSlot: SingleCourseProgramSlot & IAPProgramSlotMeta
  ) {
    const isPgmSlotMet = APProgramSlotServant.isProgramSlotMET(programSlot);
    if (isPgmSlotMet) {
      return true;
    }
    if (plannedCourse) {
      return APCourseServant.isLockedByStatus(plannedCourse.status);
    }
    return false;
  }

  getMarkAsMetMenuStatus(
    plannedCourse: APPlannedCourse & IAPPlannedCourseMeta,
    programSlot?: SingleCourseProgramSlot & IAPProgramSlotMeta
  ) {
    if (!programSlot) {
      return [false, false];
    }
    return [
      this.ngAPDegreeMapQueryHelper.showMarkAsMetMenu(programSlot, plannedCourse),
      this.ngAPDegreeMapQueryHelper.showMarkAsUnmetMenu(programSlot, plannedCourse)
    ];
  }

  getMenuOptionVisibiltyStatus() {
    const courseDetailsInfo = _4.get(this.$state, 'params.courseDetails', {});
    let showSmartCatAddToTermOption = true;
    let showSmartCatRemoveFromTermOption = true;
    const isFromSmartCatelog = _4.get(courseDetailsInfo, 'isFromSmartCat', false);
    if (isFromSmartCatelog) {
      if (this.$state.params.termId > 0) {
        showSmartCatAddToTermOption = false;
      } else {
        showSmartCatRemoveFromTermOption = false;
        const plannedCourseIds = _4.map(
          this.ngAPPlannedCoursesDatasource.values(),
          (plannedCourse) => plannedCourse.course
        );
        const plannedCoursesData = _4.filter(this.ngAPCoursesDatasource.values(), (course) =>
          _4.includes(plannedCourseIds, course.id)
        );
        if (!_4.isEmpty(plannedCoursesData)) {
          const isCourseAlreadyPlanned = !_4.isEmpty(
            _4.find(
              plannedCoursesData,
              (plannedCourse) => plannedCourse.nk === this.$state.params.courseDetails.courseNk
            )
          );
          if (isCourseAlreadyPlanned) {
            showSmartCatAddToTermOption = false;
          }
        }
      }
      return { showSmartCatAddToTermOption, showSmartCatRemoveFromTermOption };
    }
    return { showSmartCatAddToTermOption, showSmartCatRemoveFromTermOption };
  }

  showAddToCourseMenu(programSlot: SingleCourseProgramSlot, isCoursePlanned: boolean, plannedCourse: APPlannedCourse) {
    const isCourseCompleted = _4.includes(['COMPLETED'], _4.get(plannedCourse, 'status', ''));
    const isMinGradeFeatureEnabled = this.appConfigModel.isFeatureEnabled('academicplan-program_slot_min_grade');
    const isMinGradeMet = APMinGradeServant.isMinGradeMet(programSlot, plannedCourse, isMinGradeFeatureEnabled);
    if (isCourseCompleted && !isMinGradeMet) {
      return true;
    }
    return !isCoursePlanned || !APCourseServant.isLockedByStatus(plannedCourse.status);
  }

  /* tslint:disable:cognitive-complexity */
  run() {
    const courseInfo = this.ngAPCourseInfoModel.read();

    const coReqHtml = ReqServant.getReqHtml(courseInfo.coreq, courseInfo.associated_course as AssociatedCourse);
    const filterTestScores = this.appConfigModel.isFeatureEnabled('cat-hide_test_node_in_prereq');
    const preReqsForHTML = filterTestScores
      ? _4.filter(courseInfo.prereq, (req: ReqNode) => !ReqServant.isTestTypeRequisite(req))
      : courseInfo.prereq;

    const preReqHtml = ReqServant.getReqHtml(preReqsForHTML, courseInfo.associated_course as AssociatedCourse);

    const coReqs = _4.toArray(courseInfo.coreq);
    const preReqs = _4.toArray(courseInfo.prereq);
    const hasTests = _4.some(preReqs, (req) => req.type === 'TEST');
    const course: APCourseDetailCourse = APCourse.build(_4.first(_4.toArray(courseInfo.course)));
    const hasPreReqIssues = _4.some(preReqs, (req) => req.is_grade_satisfied !== null && !req.is_grade_satisfied);
    let multiSectionLabel = ``;
    let hasMultiSections = false;
    let determinedPlannedCourse: APPlannedCourse & IAPPlannedCourseMeta = {} as APPlannedCourse & IAPPlannedCourseMeta;
    const firstPlannedCourse: APPlannedCourse = _4.head(_4.toArray(courseInfo.planned_course)) || {};
    this.isFromSimpleSmartCatSearch = _4.get(this.$state, 'params.courseDetails.allowAddToTerm', false);
    if (courseInfo.termId !== INVALID_ID) {
      determinedPlannedCourse = this.ngAPDegreeMapQueryHelper.getPlannedCourseByCourseIdAndTermId(
        course.id,
        courseInfo.termId
      );
    }
    // Fallback logic
    if (!this.isFromSimpleSmartCatSearch && _4.isEmpty(determinedPlannedCourse)) {
      const existingPlannedCourse = _4.isEmpty(firstPlannedCourse)
        ? this.ngAPDegreeMapQueryHelper.getRelatedPlannedCourse(course.id)
        : this.ngAPDegreeMapQueryHelper.getPlannedCoursebyId(firstPlannedCourse.id);
      if (!_4.isEmpty(existingPlannedCourse)) {
        determinedPlannedCourse = existingPlannedCourse;
      }
    }

    let programSlot: SingleCourseProgramSlot & IAPProgramSlotMeta;
    if (!_4.isEmpty(determinedPlannedCourse)) {
      hasMultiSections = _4.get(determinedPlannedCourse, '_hasMultiSections');
      multiSectionLabel = _4.get(determinedPlannedCourse, '_multiSectionLabels', '');
      programSlot = _4.find(this.ngAPProgramSlotDatasource.metasource().derived(), {
        id: _4.get(determinedPlannedCourse, 'program_slot', INVALID_ID)
      });
    } else {
      programSlot = _4.find(this.ngAPProgramSlotDatasource.metasource().derived(), {
        id: _4.get(courseInfo, 'programSlot', INVALID_ID)
      });
    }

    const [showMarkAsMetMenu, showMarkAsUnmetMenu] = this.getMarkAsMetMenuStatus(determinedPlannedCourse, programSlot);

    const isLockedByStatus = this.isLockedByStatus(determinedPlannedCourse, programSlot);
    const { isCourseFlagged, flagComment } =
      this.ngAPDegreeMapQueryHelper._getPlannedFlaggingInfo(determinedPlannedCourse);
    const isPlannedSomewhere = _4.isEmpty(determinedPlannedCourse)
      ? false
      : determinedPlannedCourse.term !== courseInfo.termId;
    const isPlanned = !!determinedPlannedCourse;
    const isMinGradeFeatureEnabled = this.appConfigModel.isFeatureEnabled('academicplan-program_slot_min_grade');
    const genericSlot = APGenericSlotBuilder.build({
      type: 'COURSE',
      title: course.title,
      course,
      status: _4.get(determinedPlannedCourse, 'status', 'DEFAULT'),
      plannedCourse: determinedPlannedCourse || {},
      termId: _4.get(determinedPlannedCourse, 'term', INVALID_ID),
      isLockedByStatus,
      isDragEnabled: false,
      isPlanned,
      credits: course.credits,
      showMarkAsMetMenu,
      showMarkAsUnmetMenu,
      isCourseCompleted: _4.includes(['COMPLETED'], _4.get(determinedPlannedCourse, 'status', '')),
      shouldShowAddToTermMenu: this.showAddToCourseMenu(programSlot, isPlanned, determinedPlannedCourse),
      isMinGradeMet: APMinGradeServant.isMinGradeMet(programSlot, determinedPlannedCourse, isMinGradeFeatureEnabled),
      isCourseFlagged,
      flagComment,
      isLockedByAdvisor: _4.get(determinedPlannedCourse, 'is_locked', false),
      programSlot: programSlot || {},
      isPlannedSomewhere
    });

    this._result = {
      course,
      coursePlanningSource: this.planningSource,
      preReqHtml,
      coReqHtml,
      hasCoReqs: coReqs.length > 0,
      hasPreReqs: preReqs.length > 0,
      hasPreReqIssues,
      hasTests,
      hasMultiSections,
      multiSectionLabel,
      genericSlot,
      ...this.getMenuOptionVisibiltyStatus()
    };

    return this;
  }

  get planningSource() {
    let source = 'PLANNER';
    const courseDetails = _4.get(this.$state, 'params.courseDetails', {});
    if (_4.get(courseDetails, 'isFromSmartCat', false) || _4.get(courseDetails, 'allowAddToTerm', false)) {
      source = 'SMART_CATALOG';
    }
    return source;
  }
  get result() {
    return this._result;
  }
}

angular.module('capability-ng-academic-planner').service('ngAPCourseInfoQuery', APCourseInfoQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/global/components/course_detail/queries/ap_course_info_query.ts